import { useState } from 'react';
import {
  PLAN_BASE_CENTS,
  PLAN_INCLUDED_SEATS,
  PLAN_SEAT_CENTS,
  formatCents,
  type CompanyAccessPayload,
} from '@fsm/shared';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Button } from './ui';
import { Icon } from './icons';

/**
 * What a company sees when it can no longer change anything.
 *
 * ## Two pieces, on purpose
 *
 * A dialog on arrival, and a strip that stays afterwards. The dialog is the
 * only moment anybody reads a full sentence; the strip is what stops the
 * state being forgotten five minutes later.
 *
 * The alternative — a strip alone — was how this behaved for exactly one
 * afternoon, and the complaint was fair: nothing announced itself, and the
 * first sign of trouble was a form refusing to save after the work of filling
 * it in. The dialog moves the bad news to before the typing.
 *
 * ## Why the dialog can be dismissed
 *
 * Because the promise made when the trial ended was that their records are
 * still theirs to look at, and a dialog with no way past it is a lockout
 * wearing a different hat. Somebody who needs a customer's phone number at
 * eight in the morning gets it; they just cannot change it.
 *
 * It is dismissed only by the button, though — not by Escape, not by clicking
 * the backdrop. Those are reflexes, and a reflex is not a decision. This
 * deliberately does NOT use the shared `Modal`, which closes on both.
 *
 * ## Why dismissal is not remembered
 *
 * It lives in component state, so it comes back on the next load. That is the
 * point: it is a notice about money, not a cookie banner, and it should cost
 * one click a day until it is dealt with.
 */

interface Copy {
  title: string;
  body: string;
  /** False where paying is not the way out. */
  payable: boolean;
}

/*
 * One per reason, because "this account is read-only" tells nobody what to do
 * next. A failed card, a cancelled subscription and a suspension are three
 * different conversations with three different next steps.
 */
const COPY: Record<CompanyAccessPayload['reason'], Copy> = {
  trial_expired: {
    title: 'Your free trial has ended',
    body: 'Everything you added during the trial is still here. Subscribe to carry on booking work, sending estimates and taking payments.',
    payable: true,
  },
  canceled: {
    title: 'Your subscription has ended',
    body: 'Your records are all still here and nothing has been deleted. Start the subscription again whenever you are ready and everything picks up where it left off.',
    payable: true,
  },
  payment_failed: {
    title: 'We could not take your payment',
    body: 'The card on file was declined and the retries have run out. Updating it brings the account straight back.',
    payable: true,
  },
  suspended: {
    title: 'This account has been suspended',
    body: 'Someone at Crewlark has put a hold on this account. Get in touch and we will sort it out with you.',
    payable: false,
  },
  /*
   * The three below never reach the dialog, because they are full access. They
   * exist so that adding a reason to the rule cannot leave a hole here: the
   * record type makes the compiler ask for it.
   */
  trialing: { title: 'Free trial', body: '', payable: true },
  subscribed: { title: 'Subscribed', body: '', payable: true },
  payment_failed_grace: { title: 'Payment failed', body: '', payable: true },
};

/**
 * Start a subscription.
 *
 * The server builds the Checkout session — including which prices and how
 * many seats — and hands back a URL. None of that is decided here, because a
 * client that could choose its own price is a client that can choose to pay
 * nothing.
 *
 * A full page navigation rather than a new tab: Stripe's page is the next
 * step of this flow, not a detour, and a popup is the thing a browser blocks.
 */
async function startCheckout(): Promise<void> {
  const { url } = await api.billing.checkout();
  window.location.href = url;
}

/** Where to write when paying is not the answer. */
const CONTACT_HREF = 'mailto:office@crew-lark.com';

/**
 * A price, without the trailing cents when there are none.
 *
 * `formatCents` is right everywhere money is being counted — an invoice line
 * reading "$99" when it means $99.00 invites somebody to wonder. A price on a
 * sign is not being counted, and "$99.00 a month" reads like a utility bill.
 */
function price(cents: number): string {
  return formatCents(cents).replace(/\.00$/, '');
}

/**
 * What the money buys, as somebody about to pay would want it listed.
 *
 * Shorter than crew-lark.com/pricing on purpose. That page is arguing with a
 * stranger who has never seen the product; this one is talking to a
 * contractor who has been using it for a fortnight and needs reminding what
 * they are about to lose. Six lines they will actually read beats twelve they
 * will scroll past.
 *
 * Every line names something that exists today. A list that promises what is
 * coming is how a subscription gets charged back.
 */
const INCLUDED = [
  'Dispatch board, jobs and customers — the whole week in one place',
  'Estimates with deposits, invoices, and card payments at the door',
  'The field app, which keeps working with no signal',
  'Recurring jobs, job costing and your own price book',
  'Email sent from your own address, not ours',
  'Every feature, every account — no upgrade to buy later',
];

/*
 * The seat rule only. The figure itself is set in type above this line, and
 * saying "$99 a month" twice in two centimetres reads as a mistake.
 */
function priceLine(): string {
  return `Includes ${PLAN_INCLUDED_SEATS} users. Each person after that is ${price(PLAN_SEAT_CENTS)} a month.`;
}

export function Paywall() {
  const { company } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  /*
   * `access` is read defensively, and that is not belt-and-braces.
   *
   * A session stored before the server learned to send it has no `access` at
   * all, and the browser keeps that session across the deploy that introduced
   * this component. Reading `.level` off undefined throws during render,
   * which React turns into a blank page for the whole app — every screen,
   * not just this strip. An account in good standing would have been locked
   * out by the thing built to avoid locking anybody out.
   *
   * Missing therefore means full access: the server refuses the writes
   * anyway, so the worst case is a company that sees no warning until its
   * next sign-in, rather than one that sees nothing at all.
   */
  const access = company?.access;
  if (!access || access.level !== 'read_only') return null;

  const copy = COPY[access.reason] ?? COPY.canceled;

  if (!dismissed) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/45 p-5 backdrop-blur-[2px]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paywall-title"
      >
        <div className="w-full max-w-[460px] rounded-2xl border border-line bg-surface p-7 shadow-2xl">
          <span className="mb-4 inline-flex size-11 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <Icon name={copy.payable ? 'card' : 'lock'} size={22} />
          </span>

          <h2 id="paywall-title" className="font-display text-[23px] leading-[1.2] font-extrabold tracking-[-0.03em] text-ink">
            {copy.title}
          </h2>
          <p className="mt-3 text-[15px] leading-[1.55] text-ink-2">{copy.body}</p>

          {copy.payable && (
            <div className="mt-5 rounded-xl border border-accent-line bg-accent-soft px-4 py-4">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-[30px] leading-none font-extrabold tracking-[-0.04em] text-ink">
                  {price(PLAN_BASE_CENTS)}
                </span>
                <span className="text-[14px] text-ink-3">a month</span>
              </div>
              <p className="mt-1.5 text-[13.5px] text-ink-3">{priceLine()}</p>

              {/*
               * The list is here because "subscribe for $99" is a price with
               * nothing attached to it, and somebody deciding whether to pay
               * is deciding what they get. Six lines, not the website's
               * twelve: this is a reminder to a person who has already used
               * the product, not a pitch to a stranger.
               */}
              <ul className="mt-4 space-y-2 border-t border-accent-line pt-3.5">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-[13.5px] leading-[1.45] text-ink-2">
                    <Icon name="check" size={15} className="mt-[2px] shrink-0 text-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2.5">
            <Button
              variant="primary"
              size="lg"
              loading={starting}
              onClick={() => {
                if (!copy.payable) {
                  window.location.href = CONTACT_HREF;
                  return;
                }
                setStarting(true);
                setFailed(null);
                /*
                 * The button stays in its loading state on success. The
                 * redirect to Stripe is already on its way, and flipping back
                 * to "Subscribe" for the half second before the page changes
                 * reads as a click that did not work — which is when people
                 * click again.
                 */
                startCheckout().catch((error: unknown) => {
                  setStarting(false);
                  setFailed(
                    error instanceof Error && error.message
                      ? error.message
                      : 'Could not reach the payment page. Try again in a moment.',
                  );
                });
              }}
            >
              {copy.payable ? `Subscribe — ${price(PLAN_BASE_CENTS)} a month` : 'Contact Crewlark'}
            </Button>

            {failed && (
              <p className="text-[13px] leading-[1.45] text-rose-700">
                {failed} If it keeps happening, email{' '}
                <a href={CONTACT_HREF} className="font-semibold underline">
                  office@crew-lark.com
                </a>
                .
              </p>
            )}
            <Button variant="ghost" size="lg" onClick={() => setDismissed(true)}>
              Carry on with read-only access
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <PaywallBanner reason={access.reason} />;
}

/**
 * The strip that stays once the dialog is gone.
 *
 * Deliberately not dismissible. The dialog was the thing that could be waved
 * away; if this could go too, a read-only account would look identical to a
 * working one until the next save failed — which is the state this whole
 * component exists to prevent.
 */
function PaywallBanner({ reason }: { reason: CompanyAccessPayload['reason'] }) {
  const copy = COPY[reason];
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-[13.5px] text-amber-900">
      <Icon name="info" size={15} className="shrink-0" />
      <span className="font-semibold">{copy.title}.</span>
      <span className="text-amber-800">Read-only until this is sorted — you can still see everything.</span>
      <button
        type="button"
        onClick={() => {
          if (!copy.payable) {
            window.location.href = CONTACT_HREF;
            return;
          }
          void startCheckout().catch(() => {
            window.location.href = CONTACT_HREF;
          });
        }}
        className="ml-auto shrink-0 font-semibold text-accent underline underline-offset-2 hover:no-underline"
      >
        {copy.payable ? `Subscribe — ${price(PLAN_BASE_CENTS)} a month` : 'Contact Crewlark'}
      </button>
    </div>
  );
}
